/**
 * SpatialHash - Efficient spatial partitioning for collision detection
 * Reduces O(n*m) collision checks to O(n+m) by only checking nearby objects
 */
export class SpatialHash {
    constructor(cellSize = 20) {
        this.cellSize = cellSize;
        this.grid = new Map();
    }
    
    /**
     * Insert an object into the spatial hash at a given position
     */
    insert(obj, position) {
        const key = this.getKey(position);
        if (!this.grid.has(key)) {
            this.grid.set(key, []);
        }
        this.grid.get(key).push(obj);
    }
    
    /**
     * Get all objects in the same cell and neighboring cells
     * @param {Object} position - {x, y, z} position
     * @param {Number} radius - How many cells to check in each direction (default 1)
     */
    getNearby(position, radius = 1) {
        const results = [];
        const cellX = Math.floor(position.x / this.cellSize);
        const cellZ = Math.floor(position.z / this.cellSize);
        
        // Check all cells in radius
        for (let dx = -radius; dx <= radius; dx++) {
            for (let dz = -radius; dz <= radius; dz++) {
                const key = `${cellX + dx},${cellZ + dz}`;
                if (this.grid.has(key)) {
                    results.push(...this.grid.get(key));
                }
            }
        }
        return results;
    }
    
    /**
     * Get grid cell key for a position
     */
    getKey(position) {
        const cellX = Math.floor(position.x / this.cellSize);
        const cellZ = Math.floor(position.z / this.cellSize);
        return `${cellX},${cellZ}`;
    }
    
    /**
     * Clear the entire grid (call this each frame before rebuilding)
     */
    clear() {
        this.grid.clear();
    }
    
    /**
     * Get statistics about the spatial hash (for debugging)
     */
    getStats() {
        let totalObjects = 0;
        let maxInCell = 0;
        
        for (const [key, objects] of this.grid.entries()) {
            totalObjects += objects.length;
            maxInCell = Math.max(maxInCell, objects.length);
        }
        
        return {
            cells: this.grid.size,
            totalObjects,
            avgPerCell: totalObjects / this.grid.size,
            maxInCell
        };
    }
}
